import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Check, Copy, ExternalLink, Info, PackageSearch } from 'lucide-react';
import { SkeletonBox } from '../SkeletonLoader';
import { copyText, safeGmailUrl, safeHttpsUrl } from '../../utils/shipmentsFormat';

// Link out to a server-provided URL; anything that fails the check is not rendered as a link.
// kind="tracking" (default): plain https, no credentials. kind="gmail": additionally the host must be exactly mail.google.com.
export const ExternalLinkButton = ({ url, kind = 'tracking', children, className = 'shp-textbtn', unavailable }) => {
  const safe = kind === 'gmail' ? safeGmailUrl(url) : safeHttpsUrl(url);
  if (!safe) return unavailable ? <span className="shp-small shp-muted">{unavailable}</span> : null;
  return (
    <a href={safe} target="_blank" rel="noopener noreferrer" className={className} style={{ textDecoration: 'none' }}>
      {children} <ExternalLink size={14} aria-hidden="true" /><span className="shp-sr"> (otevře se v novém okně)</span>
    </a>
  );
};

const BANNER_ICONS = { info: Info, warn: AlertTriangle, bad: AlertCircle, ok: CheckCircle2 };

// tone: info | warn | bad | ok. Errors get role="alert", the rest role="status".
export const Banner = ({ tone = 'info', children, action, role }) => {
  const Icon = BANNER_ICONS[tone];
  return (
    <div className={`shp-banner shp-banner--${tone}`} role={role || (tone === 'bad' ? 'alert' : 'status')}>
      <Icon size={18} aria-hidden="true" />
      <div className="shp-banner__body shp-wrap">{children}</div>
      {action}
    </div>
  );
};

export const EmptyState = ({ icon = PackageSearch, title, children, action }) => {
  const Icon = icon; // capitalised local, so the plain no-unused-vars rule sees the JSX use
  return (
    <div className="shp-card shp-empty">
      <Icon size={40} aria-hidden="true" />
      <p style={{ fontSize: 16, fontWeight: 600, color: '#333', margin: '0 0 6px' }}>{title}</p>
      {children && <p style={{ margin: action ? '0 0 16px' : 0, fontSize: 14 }}>{children}</p>}
      {action}
    </div>
  );
};

// Copies a value; the icon turns into a check for 1.5 s and the change is announced to screen readers.
export const CopyButton = ({ value, label = 'Kopírovat číslo zásilky' }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const onClick = async (e) => {
    e.stopPropagation();
    if (await copyText(value)) {
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    }
  };
  return (
    <button type="button" className="shp-iconbtn" onClick={onClick} aria-label={label} title={copied ? 'Zkopírováno' : label}>
      {copied ? <Check size={15} color="#2E7D32" aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
      <span className="shp-sr" role="status">{copied ? 'Zkopírováno' : ''}</span>
    </button>
  );
};

export const SkeletonList = ({ rows = 6 }) => (
  <div className="shp-card shp-card--flush" aria-busy="true" aria-label="Načítám">
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} style={{ display: 'flex', gap: 16, padding: '16px 14px', borderBottom: '1px solid #F0F0F0', alignItems: 'center' }}>
        <SkeletonBox width="96px" height="22px" style={{ borderRadius: 11 }} />
        <SkeletonBox width="22%" height="16px" />
        <SkeletonBox width="14%" height="14px" style={{ marginLeft: 'auto' }} />
        <SkeletonBox width="12%" height="14px" />
      </div>
    ))}
  </div>
);

export const SkeletonBlock = ({ height = 180 }) => (
  <div className="shp-card" aria-busy="true" aria-label="Načítám">
    <SkeletonBox width="40%" height="18px" style={{ marginBottom: 14 }} />
    <SkeletonBox height={`${height}px`} />
  </div>
);
